"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowRightLeft,
  Hand,
  Users,
} from "lucide-react";
import MarketingNav from "~/app/components/marketing-nav";
import MarketingFooter from "~/app/components/marketing-footer";
import {
  BookDemoCTA,
  DemoCTA,
  PrimaryCTA,
  SiteGlueCtaButton,
  TrishCTA,
} from "~/components/siteglue/siteglue-cta";
import { MarketingExploreCta } from "~/components/inbound-gpt/MarketingExploreCta";
import { Button } from "~/components/ui/button";
import { AI_BUILT_FOR_HUMANS_LINE } from "~/lib/siteglue/ai-employee-plans";
import { getMarketingChrome, SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import { SITEGLUE_PAGE_STORY } from "~/lib/siteglue/page-story";

type JourneyMilestone = {
  year: string;
  title: string;
  body: string;
  /** Optional pull quote shown after this milestone */
  pullQuote?: string;
  highlight?: boolean;
};

const JOURNEY: JourneyMilestone[] = [
  {
    year: "1984",
    title: "The Beginning",
    body: "My fascination with technology began and I discovered the power of software to help people accomplish more.",
  },
  {
    year: "1990s",
    title: "Building the Early Web",
    body: "Helped businesses embrace the web and create better digital experiences.",
    pullQuote: "Technology only matters if it helps people do better work.",
  },
  {
    year: "2000s",
    title: "Helping Businesses Grow",
    body: "Focused on customer engagement, digital marketing, and creating better conversations between businesses and customers.",
  },
  {
    year: "2016",
    title: "Founded SiteGlue",
    body: "Started SiteGlue to help organizations answer questions faster, capture leads, and improve customer communication.",
  },
  {
    year: "2023",
    title: "The AI Revolution",
    body: "Recognized that generative AI could become an AI teammate instead of just another chatbot.",
    pullQuote:
      "The question wasn’t whether AI could talk. It was whether it could work with people.",
  },
  {
    year: "2025",
    title: "AI Employees",
    body: "Expanded SiteGlue into an AI employee platform that works across phone, website chat, SMS, email, video calls, WhatsApp, and Messenger.",
  },
  {
    year: "Today",
    title: "Human-in-the-Loop™",
    body: "Every feature is designed around one philosophy: AI should automate repetitive work while humans remain in control.",
    highlight: true,
  },
];

const DIFFERENTIATORS = [
  {
    icon: ArrowRightLeft,
    title: "Team Handoff™",
    body: "When your AI reaches the limits of automation, it hands the conversation to the right person—with context intact.",
  },
  {
    icon: Hand,
    title: "Human-in-the-Loop™",
    body: "Review, monitor, and guide every AI conversation. Your team stays accountable for judgment and relationships.",
  },
  {
    icon: Users,
    title: "Team Takeover™",
    body: "Instantly continue any conversation—phone, website chat, SMS, email, video calls, WhatsApp, or Messenger—without asking the customer to start over.",
  },
] as const;

function FadeIn({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function JourneyToHitlSection() {
  return (
    <section
      id="journey"
      data-testid="section-journey-to-hitl"
      className="relative overflow-hidden border-b border-slate-200/80 bg-[#fafbfc] py-20 sm:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background: `radial-gradient(ellipse at 50% 0%, ${SITEGLUE_COLORS.blue}12, transparent 55%)`,
        }}
      />

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <FadeIn className="mx-auto max-w-2xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
            {AI_BUILT_FOR_HUMANS_LINE}
          </p>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            Journey to Human-in-the-Loop™
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Decades of building software for people—leading to one clear idea about how AI should
            work.
          </p>
        </FadeIn>

        <div className="relative mx-auto mt-16 max-w-3xl lg:mt-20">
          {/* Continuous connecting line */}
          <div
            className="absolute bottom-8 left-[15px] top-3 w-px bg-gradient-to-b from-[#4f81bd]/50 via-slate-200 to-[#9cbb5a]/60 sm:left-[19px]"
            aria-hidden
          />

          <ol className="relative space-y-0">
            {JOURNEY.map((step, index) => (
              <li key={`${step.year}-${step.title}`} className="relative">
                <motion.div
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
                  className="relative flex gap-5 pb-12 sm:gap-8 sm:pb-16"
                >
                  <div className="relative z-10 flex shrink-0 flex-col items-center">
                    <span
                      className={`mt-1 flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white shadow-sm sm:h-10 sm:w-10 ${
                        step.highlight
                          ? "border-[#9cbb5a] ring-4 ring-[#9cbb5a]/15"
                          : "border-[#4f81bd]"
                      }`}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full sm:h-3 sm:w-3 ${
                          step.highlight ? "bg-[#9cbb5a]" : "bg-[#4f81bd]"
                        }`}
                      />
                    </span>
                  </div>

                  <div
                    className={`min-w-0 flex-1 ${
                      step.highlight
                        ? "rounded-2xl border border-[#9cbb5a]/25 bg-white p-6 shadow-sm sm:p-8"
                        : "pt-0.5"
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <p
                        className={`font-bold tracking-tight ${
                          step.highlight
                            ? "text-3xl text-[#6b8f2e] sm:text-4xl"
                            : "text-3xl text-[#4f81bd] sm:text-4xl"
                        }`}
                      >
                        {step.year}
                      </p>
                      {index === 0 ? (
                        <Image
                          src="/siteglue/jim-wanek.png"
                          alt=""
                          width={40}
                          height={40}
                          className="h-10 w-10 rounded-full border border-slate-200 object-cover object-[center_20%] shadow-sm"
                        />
                      ) : null}
                    </div>
                    <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg">
                      {step.body}
                    </p>
                  </div>
                </motion.div>

                {step.pullQuote ? (
                  <motion.blockquote
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: 0.08 }}
                    className="relative mb-12 ml-12 border-l-2 border-[#4f81bd]/30 pl-6 sm:mb-16 sm:ml-[4.5rem] sm:pl-8"
                  >
                    <p className="text-xl font-medium leading-snug text-slate-800 sm:text-2xl">
                      “{step.pullQuote}”
                    </p>
                  </motion.blockquote>
                ) : null}
              </li>
            ))}
          </ol>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55 }}
            className="relative ml-12 sm:ml-[4.5rem]"
          >
            <p className="max-w-xl text-2xl font-semibold leading-snug tracking-tight text-slate-900 sm:text-3xl">
              The future isn&apos;t AI replacing people. It&apos;s AI helping people.
            </p>
            <p className="mt-4 text-sm font-medium uppercase tracking-[0.14em] text-[#4f81bd]">
              Always a Human in the Loop™
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SiteGlueAbout() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200/80">
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute left-1/2 top-0 h-[520px] w-[820px] -translate-x-1/2 rounded-full opacity-70 blur-[120px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.blue}16, transparent 60%)`,
            }}
          />
          <div
            className="absolute bottom-0 right-0 h-[280px] w-[420px] rounded-full opacity-50 blur-[100px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.green}18, transparent 70%)`,
            }}
          />
        </div>

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#4f81bd]"
            >
              {SITEGLUE_PAGE_STORY.about.navLabel}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.02 }}
              className="mb-5 text-base font-medium text-slate-500"
            >
              {SITEGLUE_PAGE_STORY.about.line}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 }}
              className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl"
            >
              Hi, I&apos;m Jim Wanek.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-6 max-w-xl text-xl leading-relaxed text-slate-600 sm:text-2xl"
            >
              I founded SiteGlue because I believe AI should help people do their best work—not
              replace them.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg"
            >
              Always a Human in the Loop™.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
            >
              <SiteGlueCtaButton
                action="discoverPlatform"
                location="hero"
                variant="secondary"
                testId="button-about-discover-platform"
              />
              <DemoCTA location="hero" testId="button-about-live-demo" />
              <Link
                href="/about/my-team"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#4f81bd]/40 hover:text-[#4f81bd]"
              >
                Meet My AI Team
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.12, duration: 0.5 }}
            className="relative mx-auto w-full max-w-[220px] sm:max-w-[240px] lg:mx-0 lg:justify-self-end"
          >
            <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50 shadow-[0_16px_48px_-28px_rgba(15,23,42,0.35)]">
              <Image
                src="/siteglue/jim-wanek.png"
                alt="Jim Wanek, Founder of SiteGlue"
                width={480}
                height={480}
                priority
                className="aspect-square h-auto w-full object-cover object-[center_18%]"
              />
            </div>
            <p className="mt-3 text-center text-sm text-slate-500 lg:text-left">
              Jim Wanek · Founder, SiteGlue
            </p>
          </motion.div>
        </div>
      </section>

      {/* My Story */}
      <section className="border-b border-slate-200/80 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              My story
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              I&apos;ve spent my career on the moments when a customer reaches out.
            </h2>
          </FadeIn>
          <FadeIn delay={0.06} className="mt-8 space-y-6 text-lg leading-relaxed text-slate-600">
            <p>
              Long before “AI employees” were a headline, I was building platforms that helped
              businesses talk to people—live chat on websites, SMS conversations, the systems that
              sit between a customer asking for help and a team trying to keep up.
            </p>
            <p>
              Those years taught me something that still guides SiteGlue: the technology only
              matters if it makes the next human interaction better. Faster answers are great.
              Missing context, dead ends, and “please hold while I transfer you into oblivion” are
              not.
            </p>
            <p>
              When modern AI assistants arrived, I got excited—and cautious. Excited because we
              could finally automate the repetitive work that burns teams out. Cautious because too
              many products treated people as optional.
            </p>
            <blockquote className="border-l-2 border-[#4f81bd]/40 pl-6 text-xl font-medium leading-snug text-slate-800">
              I became passionate about AI assistants for one reason: done right, they give your
              team time back—for the conversations that actually need a human.
            </blockquote>
          </FadeIn>
        </div>
      </section>

      {/* The Problem I Saw */}
      <section className="border-b border-slate-200/80 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              The problem I saw
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Businesses weren&apos;t failing because they didn&apos;t care.
            </h2>
          </FadeIn>
          <FadeIn delay={0.06} className="mt-8 space-y-6 text-lg leading-relaxed text-slate-600">
            <p>
              They were drowning in volume. Missed calls after hours. The same questions asked a
              hundred times a week. Good people stretched thin between the front desk, the inbox,
              and everything else that can&apos;t wait.
            </p>
            <p>
              Hiring more staff isn&apos;t always an option. Ignoring customers isn&apos;t either.
              And bolting on a chatbot that can&apos;t hand off cleanly just creates a new kind of
              frustration—for the customer and for the team that has to clean it up.
            </p>
            <p>
              What I kept hearing was: “We need help with the repetitive work, but we still need a
              real person when it matters.” That gap is why SiteGlue exists.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* My Philosophy */}
      <section className="relative overflow-hidden border-b border-slate-200/80 bg-[#fafbfc] py-20 sm:py-28">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background: `radial-gradient(ellipse at 20% 0%, ${SITEGLUE_COLORS.blue}14, transparent 50%)`,
          }}
        />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              My philosophy
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Human-in-the-Loop™ is the core of SiteGlue.
            </h2>
          </FadeIn>
          <FadeIn delay={0.06} className="mt-8 space-y-6 text-lg leading-relaxed text-slate-600">
            <p>
              AI should automate the repetitive work—answering common questions, capturing leads,
              booking appointments, keeping conversations moving when your team is busy.
            </p>
            <p>
              Humans should remain in control—reviewing, monitoring, guiding, and stepping in when
              judgment, empathy, or accountability is required.
            </p>
            <blockquote className="rounded-2xl border border-slate-200/80 bg-white p-8 text-xl font-medium leading-snug text-slate-800 shadow-sm">
              Autonomous when it should be. Human when it matters.
              <span className="mt-4 block text-base font-normal text-slate-500">
                That isn&apos;t a feature checklist. It&apos;s the design principle behind everything
                we build.
              </span>
            </blockquote>
          </FadeIn>
        </div>
      </section>

      {/* Human + AI */}
      <section className="border-b border-slate-200/80 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              Human + AI
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              AI should augment people—not replace them.
            </h2>
          </FadeIn>
          <FadeIn delay={0.06} className="mt-8 space-y-6 text-lg leading-relaxed text-slate-600">
            <p>
              The best outcomes I&apos;ve seen aren&apos;t “AI instead of a team.” They&apos;re AI
              working alongside a team: handling the routine so people can focus on the work that
              requires experience, trust, and care.
            </p>
            <p>
              Your customers still want to feel heard by a person when the stakes are high. Your
              staff still own the relationships. SiteGlue is built so those two truths can coexist
              with modern automation.
            </p>
            <p className="text-xl font-medium text-slate-800">
              AI Built for Humans. Always a Human in the Loop™.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* What Makes SiteGlue Different */}
      <section className="border-b border-slate-200/80 bg-[#fafbfc] py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              What makes SiteGlue different
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Built so your team stays in the conversation.
            </h2>
          </FadeIn>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {DIFFERENTIATORS.map((item, index) => (
              <FadeIn key={item.title} delay={index * 0.06}>
                <article className="flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm">
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-[#4f81bd]/10 text-[#4f81bd]">
                    <item.icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                    {item.title}
                  </h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-slate-600">
                    {item.body}
                  </p>
                </article>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.15} className="mt-10 text-center">
            <Link
              href="/features#human-in-the-loop"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#4f81bd] transition hover:text-[#3d6a9e]"
            >
              See how it works on Features
              <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Looking Ahead */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              Looking ahead
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              The future belongs to organizations where humans and AI work together.
            </h2>
          </FadeIn>
          <FadeIn delay={0.06} className="mt-8 space-y-6 text-lg leading-relaxed text-slate-600">
            <p>
              I don&apos;t believe the winning companies will be the ones that remove people from
              the loop. I believe they&apos;ll be the ones that give people better tools—so every
              conversation starts informed, every handoff keeps context, and every customer still
              has a path to a human.
            </p>
            <p>
              That&apos;s the company I&apos;m building. If that resonates with how you want to
              serve your customers, I&apos;d love for you to try SiteGlue.
            </p>
          </FadeIn>

          <FadeIn delay={0.1} className="mt-12">
            <div className="flex items-center gap-5">
              <Image
                src="/siteglue/jim-wanek.png"
                alt=""
                width={72}
                height={72}
                className="h-[72px] w-[72px] rounded-full border border-slate-200 object-cover object-[center_20%] shadow-sm"
              />
              <div>
                <p className="text-lg font-medium text-slate-900">— Jim Wanek</p>
                <p className="text-slate-500">Founder, SiteGlue</p>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.14} className="mt-14">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Build the future of work with us.
            </h2>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-600">
              Give your team an AI employee designed to help people—not replace them.
            </p>
            <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <PrimaryCTA location="final_cta" testId="button-about-start-free" />
              <BookDemoCTA location="final_cta" testId="button-about-book-demo" />
            </div>
            <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/80 p-6 sm:p-8">
              <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                Ready to see where AI can help your business?
              </h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
                Trish will analyze your business and recommend the right AI Employees, channels, and
                plan—then create your personalized AI Opportunity Report.
              </p>
              <div className="mt-5">
                <TrishCTA location="about_supporting" testId="button-about-start-with-trish" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Journey timeline — below Looking Ahead */}
      <JourneyToHitlSection />

      <MarketingFooter isLanding={false} />
    </div>
  );
}

/** Inbound GPT About — product-oriented (non–SiteGlue hosts). */
function InboundAbout() {
  const chrome = getMarketingChrome();

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-background to-background py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-wide text-primary">
              {chrome.tagline}
            </p>
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-6xl">
              AI agents that never miss a conversation
            </h1>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-muted-foreground md:text-2xl">
              {chrome.productName} helps businesses deploy AI employees for phone, chat, SMS, and
              email — with a human always in the loop for transfers, judgment, and relationships.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link href="/agents">
                <Button size="lg" className="gap-2">
                  Browse AI agents
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="/help-center/human-in-the-loop">
                <Button size="lg" variant="outline">
                  Human in the Loop guides
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl space-y-6">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">About Us</h2>
            <p className="text-lg leading-relaxed text-muted-foreground">
              We built {chrome.productName} because businesses need inbound coverage without adding
              headcount — and without black-box chatbots that strand customers. Our agents answer,
              qualify, book appointments, and hand off cleanly so your team stays in control.
            </p>
            <p className="text-lg leading-relaxed text-muted-foreground">
              Founded by Jim Wanek, {chrome.productName} is built on years of experience building
              customer communication platforms—and a belief that AI should augment people, not
              replace them.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t bg-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-10 text-center text-3xl font-bold tracking-tight md:text-4xl">
              What makes {chrome.productName} different
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {DIFFERENTIATORS.map((item) => (
                <div key={item.title} className="rounded-xl border border-border bg-card p-6">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-14">
              <MarketingExploreCta testId="about-explore-cta" />
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter isLanding={false} />
    </div>
  );
}

export default function AboutUs() {
  const isSiteGlue = getMarketingChrome().brandId === "siteglue";
  return isSiteGlue ? <SiteGlueAbout /> : <InboundAbout />;
}
